import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../../contexts/NotyfContext";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import useDeviceType from "../../../hooks/useDeviceType";
import {
  getPlatormConfigPreferences,
  updatePlatformConfigPreferences,
} from "../../../redux/slices/preferences";
import * as Yup from "yup";
import { Card, Form, Button, Collapse } from "react-bootstrap";
import { Formik } from "formik";
import Loader from "../../../components/spinners/Loader";

const PlatformConfigSettings = ({ activeTab }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const platformConfigPreferences = useSelector(
    (state) => state.preferences.platformConfigPreferences
  );
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const { backgroundColor, fontColor, accentColor } = useWhitelabel();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  useEffect(() => {
    const fetchPlatformConfig = async () => {
      setIsLoading(true);
      await dispatch(getPlatormConfigPreferences())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 4500);
        });
    };

    if (user?.id && activeTab === "#PlatformConfig") {
      fetchPlatformConfig();
    }
  }, [user, activeTab, dispatch, notyf]);

  const initialValues = {
    dataStreamingEnabled: platformConfigPreferences.dataStreamingEnabled,
    maintenanceMode: platformConfigPreferences.maintenanceMode,
    maintenanceMessage: platformConfigPreferences.maintenanceMessage,
  };

  const validation = Yup.object().shape({
    dataStreamingEnabled: Yup.boolean(),
    maintenanceMode: Yup.boolean(),
    maintenanceMessage: Yup.string().nullable(),
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t("settings.platformConfig.title")}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && <Loader />}
        {!isLoading && (
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              await dispatch(updatePlatformConfigPreferences({ data: values }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Platform Configuration updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: "Something went wrong",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
              isSubmitting,
            }) => {
              const resetToInitial = () => setValues(initialValues);
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="dataStreamingEnabled">
                    <Form.Check
                      type="switch"
                      name="dataStreamingEnabled"
                      label={t("settings.platformConfig.dataStreamingEnabled")}
                      checked={values.dataStreamingEnabled}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.dataStreamingEnabled &&
                        !!errors.dataStreamingEnabled
                      }
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dataStreamingEnabled}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="maintenanceMode">
                    <Form.Check
                      type="switch"
                      name="maintenanceMode"
                      label={t("settings.platformConfig.maintenanceMode")}
                      checked={values.maintenanceMode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.maintenanceMode && !!errors.maintenanceMode
                      }
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.maintenanceMode}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Collapse in={values.maintenanceMode} dimension="height">
                    <Form.Group className="mb-4" controlId="maintenanceMessage">
                      <Form.Label>
                        {t("settings.platformConfig.maintenanceMessage")}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={isDesktop ? 4 : 3}
                        name="maintenanceMessage"
                        value={values.maintenanceMessage}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t(
                          "settings.platformConfig.maintenanceMessagePlaceholder"
                        )}
                        isInvalid={
                          touched.maintenanceMessage &&
                          !!errors.maintenanceMessage
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.maintenanceMessage}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Collapse>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="primary"
                      type="button"
                      onClick={resetToInitial}
                      disabled={isSubmitting}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                      }}
                    >
                      {isSubmitting ? "Saving" : "Save"}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </Card.Body>
    </Card>
  );
};

export default PlatformConfigSettings;
